import { evalCondition } from './cds.js';
import { conceptOf, hoursBetween, observationValue } from './context.js';
import { evaluate, parse } from './expr.js';
import { SYS_PROGRAMME } from './fhir.js';
/** Ethiopian clock: 06:00 is 12:00 LD (day), 18:00 is 12:00 LN (night), as the guide is written. */
export function ecClock(iso) {
    const h = Number(iso.slice(11, 13));
    const m = iso.slice(14, 16);
    return `${(h + 6) % 12 || 12}:${m} ${h >= 6 && h < 18 ? 'LD' : 'LN'}`;
}
/** "value < 110 or value >= 160" -> "<110, ≥160"; "value == 'N'" -> "N". */
export function thresholdLabel(expr) {
    if (!expr)
        return '—';
    return expr
        .split(/\s+or\s+/)
        .map((p) => p
        .replace(/value\s*/g, '')
        .replace(/==\s*/g, '')
        .replace(/>=\s*/g, '≥')
        .replace(/<=\s*/g, '≤')
        .replace(/([<>])\s*/g, '$1')
        .replace(/'/g, '')
        .trim())
        .join(', ');
}
/** Hours of active labour at a column ("h3.5"), or null while latent. */
export function labourHour(grid, at) {
    if (!grid.activeSince)
        return null;
    const h = hoursBetween(grid.activeSince, at);
    return h >= 0 ? Math.round(h * 10) / 10 : null;
}
/**
 * The grid of a series form from the record: only this form's assessments (another form may
 * record the same concept), within the form's window before `now`, oldest first.
 */
export function seriesGrid(pack, form, record, now, formOf) {
    const concepts = new Map(pack.concepts.map((c) => [c.code, c]));
    const fields = new Set(form.sections.flatMap((s) => s.fields.map((f) => f.concept)));
    const ofForm = new Set(record.encounters.filter((e) => formOf(e) === form.id).map((e) => `Encounter/${e.id}`));
    const byEnc = new Map();
    for (const o of record.observations) {
        const code = conceptOf(o);
        const enc = o.encounter?.reference;
        if (!code || !enc || !ofForm.has(enc) || !fields.has(code) || o.status === 'entered-in-error')
            continue;
        const h = hoursBetween(o.effectiveDateTime, now);
        if (h < 0 || h > form.series.window_hours)
            continue;
        byEnc.set(enc, [...(byEnc.get(enc) ?? []), o]);
    }
    const cols = [...byEnc.entries()]
        .map(([enc, obs]) => ({
        enc,
        at: obs[0]?.effectiveDateTime ?? '',
        values: new Map(obs.map((o) => [conceptOf(o) ?? '', observationValue(o)])),
        obsIds: new Set(obs.map((o) => o.id)),
    }))
        .sort((a, b) => a.at.localeCompare(b.at));
    const activeSince = [...cols]
        .reverse()
        .find((c) => typeof c.values.get('active_labour_at') === 'string')
        ?.values.get('active_labour_at');
    const sections = form.sections
        .map((s) => ({
        title: s.title,
        rows: s.fields
            .map((f) => concepts.get(f.concept))
            .filter((c) => !!c && c.type !== 'datetime')
            .filter((c) => cols.some((col) => col.values.has(c.code)))
            .map((c) => ({
            concept: c,
            threshold: thresholdLabel(c.abnormal),
            cells: cols.map((col) => {
                const v = col.values.get(c.code);
                const empty = v === undefined || v === null || v === '';
                return {
                    value: empty ? undefined : v,
                    alert: !empty && !!c.abnormal && evalCondition(c.abnormal, { value: v }),
                };
            }),
        })),
    }))
        .filter((s) => s.rows.length);
    return { cols, activeSince, sections };
}
const minutesBetween = (from, to) => Math.floor(hoursBetween(from, to) * 60);
/**
 * Where the series stands at `now`: over or not, first or second stage, and which of the guide's
 * scheduled assessments are due (the e-partograph's "update due"; alerts end at the delivery, so a
 * past labour never raises a new one).
 */
export function seriesState(form, grid, record, now) {
    const first = grid.cols[0];
    const last = grid.cols[grid.cols.length - 1];
    const none = { ended: null, secondStage: false, due: [] };
    if (!first || !last)
        return none;
    let ended = null;
    const ends = form.series.ends;
    if (ends?.concept)
        for (const o of record.observations) {
            if (conceptOf(o) !== ends.concept || o.status === 'entered-in-error')
                continue;
            const v = observationValue(o);
            const at = typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(v) ? v : o.effectiveDateTime;
            if (at && hoursBetween(first.at, at) >= 0 && (!ended || at < ended.at))
                ended = { at, by: 'concept' };
        }
    if (!ended && ends?.programme)
        for (const e of record.episodes ?? []) {
            const of = (e.type ?? []).some((t) => t.coding?.some((c) => c.system === SYS_PROGRAMME && c.code === ends.programme));
            const end = e.period?.end;
            if (of &&
                e.status !== 'active' &&
                e.status !== 'entered-in-error' &&
                end &&
                hoursBetween(first.at, end) >= 0)
                ended = { at: end, by: 'programme' };
        }
    const latest = Object.fromEntries(last.values);
    const secondStage = form.series.second_stage
        ? evaluate(parse(form.series.second_stage), latest) === true
        : false;
    if (ended || !grid.activeSince)
        return { ended, secondStage, due: [] };
    const lastOf = (concepts) => [...grid.cols].reverse().find((c) => concepts.some((k) => c.values.has(k)))?.at ?? null;
    const latestValue = (concept) => [...grid.cols]
        .reverse()
        .find((c) => c.values.has(concept))
        ?.values.get(concept);
    const due = [];
    for (const d of form.series.due ?? []) {
        if (d.while && latestValue(d.while) !== true)
            continue;
        const every = secondStage
            ? d.second_stage_min === undefined
                ? d.every_min
                : d.second_stage_min
            : d.every_min;
        if (!every)
            continue;
        const lastAt = lastOf(d.concepts);
        const since = lastAt ?? grid.activeSince;
        const overdue = minutesBetween(since, now) - every;
        if (overdue >= 0)
            due.push({ title: d.title, lastAt, everyMin: every, overdueMin: overdue });
    }
    due.sort((a, b) => b.overdueMin - a.overdueMin);
    return { ended, secondStage, due };
}
//# sourceMappingURL=series.js.map